import _ from "lodash";
import appConfig from "../appconfig";
import cryptoJS from "crypto-js";
import { store } from "../store";
import { getProductPrice } from "./tenantSpecific/tid6/utils";
import ProductSelection from "../domains2/orders/ProductSelection";
import * as mutationTypes from "../store/mutation-types";
import {formatPriceZero} from "../domains2/libraries/Utils";
const tenantConfig = appConfig.tenantConfig;
const { Formats: timeBoxFormats, timebox: timeBox } = appConfig.timeBoxImpl;
import LoggerProvider from "../providers/LoggerProvider";
import {isNoThanksProduct} from "../helper/ProductSelectionHelper";
import OrderHelperObject from "../domains2/orders/OrderHelperObject";
import { checkIfStringIsDate } from "../helper/PriceRulesHelper";
import { App as NativeApp } from "@capacitor/app";
import { FCM } from "@capacitor-community/fcm";
import { FirebaseAnalytics } from "@capacitor-community/firebase-analytics";
import {getDeliveryMethodByNumberValue} from "../domains2/libraries/DeliveryMethods";
import { CUSTOMER_TYPES } from "../domains2/constants/customerTypes";

export const isAppActive = () => {
	return new Promise((resolve) => {
		if (!appConfig.mobile.isMobileApp()) resolve(true);
		const interval = setInterval(async () => {
			const { isActive } = await NativeApp.getState();
			if (isActive) {
				clearInterval(interval);
				resolve();
			}
		}, 100);
	});
};

export const checkIsCustomAsapTime = function (closingTime, asapWaitingTime, TimeBoxHelper) {
	if(!closingTime || !TimeBoxHelper) {
		return false;
	}
	const hourAndMinute = closingTime.split(":");
	if(hourAndMinute.length < 2) {
		return false;
	}
	const now = TimeBoxHelper
		.nowInTimeZone()
		.exec();
	const lastPickupDateTime = TimeBoxHelper.nowInTimeZone()
		.set({
			hour: hourAndMinute[0],
			minute: hourAndMinute[1],
			millisecond: 0
		})
		.exec();
	const timeDifference = lastPickupDateTime.diff(now).toMillis();
	return timeDifference > 0 && timeDifference / 1000 / 60 <= asapWaitingTime.minutes;
};

export const shouldApplyCustomAsapTime = function(getters, asapWaitingTime) {
	const pickupRules = getters.cachedPickupRules;
	const TimeBoxHelper = getters.ShopTimeBoxHelper;

	const shop = getters.globalSelectedShop;

	if (!shop || appConfig.getBusinessModelId() === appConfig.businessModelsEnum.bm2.id) {
		return false;
	}

	const latestTime = OrderHelperObject.getLatestPickupTime({
		boxDate: timeBox.now(),
		products: getters.cartProducts || [],
		tenantData: tenantConfig,
		pickupDateTimeRules: pickupRules,
		shopData: shop,
		includePastTimes: false,
		ignoreOffset: true,
		pickupTimeZone: shop.shopTimeZone,
		useShopOpeningHoursByWeekday: getters.featureToggles.useShopOpeningHours
	});
	return checkIsCustomAsapTime(latestTime, asapWaitingTime, TimeBoxHelper);
};

export const isValidHhMm = (value) => {
	return /^([0-1]?[0-9]|2[0-4]):([0-5][0-9])(:[0-5][0-9])?$/.test(value);
};

export const calculatePercentage = function(value, percentage) {
	percentage = percentage ? percentage : 0;
	return (value * percentage) / 100;
};

export const formatCurrency = function(value, vat) {
	vat = vat ? vat : 0;

	if ((value % 1 !== 0) || (calculatePercentage(value, vat) % 1 !== 0)) {
		return (value + calculatePercentage(value, vat)).toFixed(2).replace(/\./g, ",");
	} else {
		return (value + calculatePercentage(value, vat))+",00";
	}
};


const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// used to retry promises
// Example:
//  const testFn = (a: number) => {
//	  const x = Math.trunc(Math.random() * 1000);
//	  console.log(x)
//	  return x % 9 === 0 ? Promise.resolve("good") : Promise.reject(1);
//  }
export const retry = (fn, options = {maxRetry: 3, currentRetry: 0, sleepLen: 100}) => (...ar) => {

	const { maxRetry, currentRetry, sleepLen } = options;
	return fn(...ar)
		.then(res => {
			return res;
		})
		.catch(e => {
			if (currentRetry < maxRetry) {
				return sleep(sleepLen).then(() => {
					return retry(fn, {...options, currentRetry: currentRetry + 1})(...ar);
				});
			} else {
				throw e;
			}
		});
};

export const range = (startDate, counter, numberOfDays) => {
	if(counter === numberOfDays) {
		return [startDate];
	}
	return [startDate, ...range(new Date(new Date(startDate).setDate(startDate.getDate() + 1)), counter + 1, numberOfDays)];
};

export const formatTelephone = (phone) => {
	return phone ? phone.trim().replace(/(.{2})/g, "$1 ").slice(0, -1) : "";
};

export const createValidationRules = function(app) {
	return {
		required: (value) => !!value || app.$t("libraries.utils.required"),
		email: (value) => {
			const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			return pattern.test(value) || app.$t("libraries.utils.invalidEmail");
		},
		repeatEmailForGuestsSignup: (value) => {
			return (app._data.userEmailForm.value === value) || app.$t("libraries.utils.invalidRepeatEmail2");
		},
		repeatEmailForVerification: (value) => {
			const email = app.$store.getters.getUserData.email;
			return (email === value) || app.$t("libraries.utils.invalidRepeatEmail");
		},
		phone: (value) => {
			const pattern = /^(((\+|00)\d{1,3})|(\((\+|00)\d{1,3})\))?[\s-]?(\d{2,}[\s-]?){3,}\d{2}$/;
			return pattern.test(value) || app.$t("libraries.utils.invalidPhone");
		}
	};
};

export const copyObject = function(source) {
	return JSON.parse(JSON.stringify(source));
};

export const getQueryString = params => Object.keys(params).map(key => {
	return encodeURIComponent(key) + "=" + encodeURIComponent(params[key]);
}).join("&");

export const getQueryParamValue = param => {
	if ("URLSearchParams" in window) {
		const url = window.location.href;
		const urlObj = new URL(url);
		const params = new URLSearchParams(urlObj.search);
		return params.get(param);
	} else {
		// Fallback for older browsers
		const regex = new RegExp("[?&]" + param + "=([^&#]*)", "i");
		const result = regex.exec(window.location.search);
		return result ? decodeURIComponent(result[1]) : null;
	}
};

export const isValidJSON = function(jsonString) {
	try {
		JSON.parse(jsonString);

		return true;
	} catch (e) {
		return false;
	}
};

export const secureStringify = function(obj) {
	try {
		return JSON.stringify(obj);
	} catch (err) {
		// We probably get a circular reference error
		const result2 = {};
		// Stringify only first level of objects to avoid circular json
		Object.keys(obj).forEach(key => {
			result2[key] = "" + obj[key];
		});
	}
};
export const isObjectOrArray = function(result) {
	const resultStr = secureStringify(result);
	const isObjOrArray = resultStr && (resultStr[0] === "{" || resultStr[0] === "[");
	return isObjOrArray;
};

/**
 * Create a simple string dump of an object.
 * */
export const simpleDump = function(object) {
	return "{ " + Object.keys(object).map(key => key + ": " + object[key]).join(", ") + " }";
};

const FILLERJPG = "filler.jpg";
export const imageRoot = (type) => {
	const isGlobalTemplate = type === "static" && tenantConfig.useGlobalTemplateImagePath;
	if (isGlobalTemplate) {
		const gtRootPath = `${store.getters.imageCDNRoot}/${type}/${appConfig.globalTemplateName}`;
		return `${gtRootPath}/images`;
	} else {
		return `${store.getters.imageCDNRoot}/${type}/${tenantConfig.tenantIdPathName}/images`;
	}
};

const imageBmRoot = (type) => {
	return `${store.getters.imageCDNRoot}/${type}/${tenantConfig.businessModelId}/images`;
};

const imageRootIdRoot = (type) => {
	return `${store.getters.imageCDNRoot}/${type}/tid${store.getters.rootTenantId}/images`;
};

const imageRootIdPaths = type => {
	return {
		"123T144": imageRootIdRoot(type) + "/123T144/",
		"41T48": imageRootIdRoot(type) + "/41T48/",
		"kiosk": imageRootIdRoot(type) + "/KIOSK/",
		"8T7": imageRootIdRoot(type) + "/8T7/",
		"1T1": imageRootIdRoot(type) + "/1T1/",
		"8T5": imageRootIdRoot(type) + "/8T5/",
		"85T72": imageRootIdRoot(type) + "/85T72/",
		"6T1": imageRootIdRoot(type) + "/6T1/",
		"6T1_small": imageRootIdRoot(type) + "/6T1_small/",
		"5T2": imageRootIdRoot(type) + "/5T2/",
		"5T2_small": imageRootIdRoot(type) + "/5T2_small/",
		"misc": imageRootIdRoot(type) + "/misc/",
		"cards": imageRootIdRoot(type) + "/cards/",
		"icons": imageRootIdRoot(type) + "/icons/"
	};
};

const imageBmPaths = type => {
	return {
		"123T144": imageBmRoot(type) + "/123T144/",
		"41T48": imageBmRoot(type) + "/41T48/",
		"kiosk": imageBmRoot(type) + "/KIOSK/",
		"8T7": imageBmRoot(type) + "/8T7/",
		"1T1": imageBmRoot(type) + "/1T1/",
		"8T5": imageBmRoot(type) + "/8T5/",
		"85T72": imageBmRoot(type) + "/85T72/",
		"6T1": imageBmRoot(type) + "/6T1/",
		"6T1_small": imageBmRoot(type) + "/6T1_small/",
		"5T2": imageBmRoot(type) + "/5T2/",
		"5T2_small": imageBmRoot(type) + "/5T2_small/",
		"misc": imageBmRoot(type) + "/misc/",
		"cards": imageBmRoot(type) + "/cards/",
		"icons": imageBmRoot(type) + "/icons/"
	};
};

const imagePaths = type => {
	return {
		"123T144": imageRoot(type) + "/123T144/",
		"41T48": imageRoot(type) + "/41T48/",
		"kiosk": imageRoot(type) + "/KIOSK/",
		"8T7": imageRoot(type) + "/8T7/",
		"1T1": imageRoot(type) + "/1T1/",
		"8T5": imageRoot(type) + "/8T5/",
		"85T72": imageRoot(type) + "/85T72/",
		"6T1": imageRoot(type) + "/6T1/",
		"6T1_small": imageRoot(type) + "/6T1_small/",
		"5T2": imageRoot(type) + "/5T2/",
		"5T2_small": imageRoot(type) + "/5T2_small/",
		"misc": imageRoot(type) + "/misc/",
		"cards": imageRoot(type) + "/cards/",
		"icons": imageRoot(type) + "/icons/"
	};
};

/**
 * Makes it possible to set a meta tag in the head programmatically
 * @param metaName
 * @param value
 */
export const setMetaAttribute = function(metaName, value) {
	const metaTags = document.getElementsByTagName("meta");
	if (metaTags && metaTags.length) {
		Array.forEach(metaTags, tag => {
			const name = tag.getAttribute("name");
			if (name && name.toLowerCase() === metaName) {
				tag.parentElement.removeChild(tag);
			}
		});
	}
	const newMeta = document.createElement("meta");
	newMeta.setAttribute("name", metaName);
	newMeta.setAttribute("content", value);
	document.head.appendChild(newMeta);
};

/**
 * Makes it possible to set a link tag in the head programmatically
 * @param metaName
 * @param value
 */
export const setLinkCanonicalAttribute = function(linkHref) {
	removeAllLinksCanonicalAttribute();

	const linkTags = document.getElementsByTagName("link");
	if (linkTags && linkTags.length) {
		Array.forEach(linkTags, tag => {
			const href = tag.getAttribute("href");
			const rel = tag.getAttribute("rel");
			if (href && href.toLowerCase() === linkHref && rel && rel.toLowerCase() === "canonical") {
				tag.parentElement.removeChild(tag);
			}
		});
	}
	const newLink = document.createElement("link");
	newLink.setAttribute("rel", "canonical");
	newLink.setAttribute("href", linkHref);
	document.head.appendChild(newLink);
};

export const removeAllLinksCanonicalAttribute = function() {
	const linkTags = document.querySelectorAll("link[rel=\"canonical\"]");
	linkTags.forEach(tag => {
		tag.parentElement.removeChild(tag);
	});
};

export const load404Page = function() {
	const baseUrl = appConfig.mobile.isMobileApp() ? appConfig.getBaseUrl() : window.location.origin;
	const lastChar = baseUrl.substr(-1);
	const formattedBaseUrl = lastChar !== "/" ? `${baseUrl}/` : baseUrl;
	setMetaAttribute("robots", "noindex");
	const newMeta = document.createElement("meta");
	newMeta.setAttribute("http-equiv", "refresh");
	newMeta.setAttribute("content", `0 url=${formattedBaseUrl}404.html`);
	document.head.appendChild(newMeta);
};

/**
 * Implement image loading using ordinary references under static data
 * @returns {*}
 */
export const imageLoaderNew = function({
	folderName,
	imageLink,
	object,
	type = "static",
	allowOriginalFormat = false,
	width,
	height
}) {
	if (typeof imageLink !== "string" || !imageLink) {
		console.log(`Image ${imageLink} not found ` + (object ? ` for object: ${object.id}, name "${object.name}"` : "")); // eslint-disable-line no-console
		console.log(new Error(folderName)); // eslint-disable-line no-console
		imageLink = FILLERJPG;
	}
	const replaceJpg = function(pngFile) {
		const index = pngFile.lastIndexOf(".");
		return pngFile.substring(0, index) + ".jpg";
	};

	const req = folderName ? imagePaths(type)[folderName] : imageRoot(type) + "/";

	const uri = encodeURI(allowOriginalFormat ? req + imageLink : req + replaceJpg(imageLink));

	const queryString = {};

	if (typeof width === "number" || typeof width === "number") {
		const getNumber = num => num ? num : 0;
		queryString.d = `${getNumber(width)}x${getNumber(height)}`;
	}

	if (Object.keys(queryString).length > 0) {
		return uri + "?" + Object.keys(queryString).map(key => {
			return `${key}=${queryString[key]}`;
		}).join("&");
	} else {
		return uri;
	}
};

export const addQueryString = function(url, data = {}) {
	if (Object.keys(data).length > 0) {
		return url + "?" + Object.keys(data).map(key => {
			return `${key}=${data[key]}`;
		}).join("&");
	} else {
		return url;
	}
};

export const getImageDomSize = ({ imageDom, sizeMultiplier = 1 }) => {
	const getNumber = num => Math.ceil(num ? num : 0);

	const { width, height } = imageDom.getBoundingClientRect();

	return {
		width: getNumber(width) * sizeMultiplier,
		height: getNumber(height) * sizeMultiplier
	};
};

/**
 * Implement image loading using ordinary references under static data
 * @deprecated - please use imageLoaderNew
 * @returns {*}
 */
export const imageLoader = function(folderName, imageLink, object, type = "static", allowOriginalFormat = false, IsRootTenantId = false) {
	if (typeof imageLink !== "string" || !imageLink) {
		console.log(`Image ${imageLink} not found ` + (object ? ` for object: ${object.id}, name "${object.name}"` : "")); // eslint-disable-line no-console
		console.log(new Error(folderName)); // eslint-disable-line no-console
		imageLink = FILLERJPG;
	}
	const replaceJpg = function(pngFile) {
		const index = pngFile.lastIndexOf(".");
		return pngFile.substring(0, index) + ".jpg";
	};

	const req = folderName ? IsRootTenantId ? imageRootIdPaths(type)[folderName] : imagePaths(type)[folderName] : IsRootTenantId ? imageRootIdRoot(type) : imageRoot(type) + "/";

	const uri = encodeURI(allowOriginalFormat ? req + imageLink : req + replaceJpg(imageLink));

	return uri;
};

export const getMiscImageUrl = function(imageName) {
	if (appConfig.isBM100()) {
		return encodeURI(imageBmPaths("static")["misc"] + imageName);
	}
	return encodeURI(imagePaths("static")["misc"] + imageName);
};

export const getIconsImageUrl = function(imageName) {
	if (appConfig.isBM100()) {
		return encodeURI(imageBmPaths("static")["icons"] + imageName.replace("images/icons/", ""));
	}
	return encodeURI(imagePaths("static")["icons"] + imageName.replace("images/icons/", ""));
};

export const getImageFolder = ({ bmId, imgUrl, bmFolderMap = {} }) => {
	const imageParts = imgUrl.split("/");
	if (imageParts.length === 2) {
		// currently type (dynamic or static) is irrelevant for this function
		// because it only needs folder names, so it's hardcoded to be dynamic
		const isFolderInUrl = Object.keys(imagePaths("dynamic")).filter(val => {
			return val === imageParts[0];
		}).length > 0;

		if (isFolderInUrl) {
			return "";
		} else {
			console.log(new Error(imageParts[0])); // eslint-disable-line no-console
		}
	} else if (imageParts.length > 2) {
		console.log(new Error(imgUrl)); // eslint-disable-line no-console
	}
	return bmFolderMap[bmId];
};

export const validateValue = function(value, rules) {
	rules = rules ? rules : [];
	return rules.reduce((carry, rule) => {
		return carry && rule(value) === true;
	}, true);
};

export const validateForm = function(form) {
	return _.reduce(form, (carry, item) => {
		return carry && validateValue(item.value, item.rules);
	}, true);
};

export const splitCamelCase = function(string) {
	return string
		? string.split(/(?=[A-Z])/)
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ")
		: "";
};

export const isValidUuid = (value) => {
	const expression =
		/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
	const regex = new RegExp(expression);
	return typeof value === "string" && value.match(regex);
};

export const setPageTitle = function(route,productCatalog) {
	if(route && route.path && !route.path.includes("admin")) {
		if(route.path === "/") {
			return document.title = "Home";
		}
		else{
			const routeSplit = route.path.split("/");
			if(routeSplit.length > 3 && route.path.includes("configuration") && !route.query.editProductId) {
				const product = productCatalog.getProduct(routeSplit[3], true);
				return document.title = product.name;
			}
			if(route.path.includes("category") && !route.query.editProductId) {
				const category = getCategoryById(routeSplit[2],productCatalog);
				return document.title = category.name;
			}
			if(route.query.editProductId) {
				const product = productCatalog.getProduct(route.query.editProductId, true);
				return document.title = product.name;
			}
			if(route.path  !== "/") {
				return document.title = splitCamelCase(route.name);
			}
		}
	}
};

// This is a quick fix which can probably be improved using this library: http://kazupon.github.io/vue-i18n/en/pluralization.html
export const translateUnit = function(unit, count) {
	if (count === 1) {
		return unit;
	} else {
		switch (unit) {
		case "kuvert":
			return "kuverter";
		case "stk":
			return "stk";
		case "menu":
			return "menu";
		default:
			return unit;
		}

	}
};

/**
 * Returns proper document title
 */
export const getDocumentTitle = function(title) {
	const mytitle = (title ? title + " " : "") + tenantConfig.tenantName + (tenantConfig.tenantId !== 3 ? " Mad ud af huset" : " ud af huset");
	if (!appConfig.isProductionEnvironment() && typeof appConfig.loadedEnvironment() === "string") {
		if (appConfig.isBM100()) {
			return appConfig.envPrefix() === "p" ? mytitle : appConfig.envPrefix() + "-" + mytitle;
		}
		return appConfig.envPrefix() + "-" + mytitle;
	} else {
		return mytitle;
	}
};

/**
 * Returns a Date a number of days from now.
 */
export const getDaysFromNow = function(days) {
	const today = timeBox.now();
	return new Date(today.year().exec(), today.month().exec(), today.day().exec() + days);
};


/**
 * Returns a date formated to be shown on the format:
 * 23-01-2023
 * @param dateStr
 * @returns {*}
 */
export const formatDate = (date) => {
	return checkIfStringIsDate(date) ? timeBox.fromISO(date).format(timeBoxFormats.DD_MM_YYYY).exec() : "";
};

/**
 * Returns a date formated to be shown on the format:
 * Mandag d. 23. oktober
 * @param vueComp
 * @param dateStr
 * @returns {*}
 */
export const formatDateFriendly = function(vueComp, dateStr) {
	const date = new Date(dateStr);
	const day = date.getDay() !== 0 ? date.getDay() : 7;
	const month = date.getMonth() + 1;
	return vueComp.$t("libraries.utils.pickupDateFormatted", {
		dayName: _.upperFirst(vueComp.$t(`common.days.day${day}`)),
		day: date.getDate(),
		monthName: vueComp.$t(`common.months.month${month}`)
	});
};

/**
 * Returns a date formated to be shown on the format:
 * Man. 23 okt
 * @param vueComp
 * @param date
 * @returns {*}
 */
export const formatDateFriendlyShort = function(vueComp, date) {
	const day = date.getDay() !== 0 ? date.getDay() : 7;
	const month = date.getMonth() + 1;
	return vueComp.$t("libraries.utils.pickupDateFormattedShort", {
		dayName: _.upperFirst(vueComp.$t(`common.days.day${day}`)).slice(0, 3),
		day: date.getDate(),
		monthName: vueComp.$t(`common.months.month${month}`).slice(0, 3)
	});
};

/**
 * Returns a date formated to be shown on the format:
 * Man. 23 okt
 * @param vueComp
 * @param date
 * @returns {*}
 */
export const formatDateTimeFriendlyShort = function(vueComp, date) {
	const day = date.getDay() !== 0 ? date.getDay() : 7;
	const month = date.getMonth() + 1;
	return vueComp.$t("libraries.utils.pickupDateTimeFormattedShort", {
		dayName: _.upperFirst(vueComp.$t(`common.days.day${day}`)).slice(0, 3),
		day: date.getDate(),
		monthName: vueComp.$t(`common.months.month${month}`).slice(0, 3),
		hour: timeBox.fromJsObject(date).format("HH:mm").exec()
	});
};

export const toIsoString = (date) => {
	const tzo = -date.getTimezoneOffset(),
		dif = tzo >= 0 ? "+" : "-",
		pad = function(num) {
			return (num < 10 ? "0" : "") + num;
		},
		padMillis = num => (num < 10 ? "00" : (num < 100 ? "0" : "")) + num;

	return date.getFullYear() +
      "-" + pad(date.getMonth() + 1) +
      "-" + pad(date.getDate()) +
      "T" + pad(date.getHours()) +
      ":" + pad(date.getMinutes()) +
      ":" + pad(date.getSeconds()) +
			"." + padMillis(date.getMilliseconds()) +
      dif + pad(Math.floor(Math.abs(tzo) / 60)) +
      ":" + pad(Math.abs(tzo) % 60);
};

/***
 * Handles a keypress event and rejects anything but numbers, tabs, enter etc.
 * @param e
 * @returns {boolean}
 */
export const validateNumberInputEvent = function(e) {
	const allowedKeys = {
		"Delete": true,
		"Backspace": true,
		"ArrowLeft": true,
		"ArrowRight": true,
		"Tab": true,
		"Enter": true,
		"ArrowUp": true,
		"ArrowDown": true
	};
	const pattern = /([\d+\-()\s])/;
	if (pattern.test(e.key) || allowedKeys[e.key]) {
		return true;
	} else {
		e.preventDefault();
		return false;
	}
};

/***
 * Handles a keypress event and rejects anything but time format inputs (HH:MM, H:MM, 0H:MM etc.)
 * @param e
 * @returns {boolean}
 */
export const validateTimeInputEvent = function(e) {
	const allowedKeys = {
		"Delete": true,
		"Backspace": true,
		"ArrowLeft": true,
		"ArrowRight": true,
		"Tab": true,
		"Enter": true,
		"ArrowUp": true,
		"ArrowDown": true
	};
	const pattern = /([\d]|[:])/;
	// const pattern = /(((1[0-9]?)|(0?[1-9])|(2[0-3]?))[:][0-5][0-9]?)/;
	if (pattern.test(e.key) || allowedKeys[e.key]) {
		return true;
	} else {
		e.preventDefault();
		return false;
	}
};


export const formatTime = function(dateTimeStr) {
	return timeBox.fromISO(dateTimeStr).format("H:mm").exec();
};

export const showCustomersData = function(string) {
	return string !== "" ? string : "N/A";
};

/**
 * Used to generate nice guids for unittest
 * @param n
 * @returns {string}
 */
export const uid = function(n) {
	const zeros = "00000000-0000-0000-0000-000000000000";
	const nstr = n + "";
	return zeros.substring(0, zeros.length - nstr.length) + nstr;
};

export const md5HashEncode = function(string) {
	if (!string) return "";
	return cryptoJS.MD5(string).toString();
};

export const hexToRGB = function(hex, alpha = 1) {
	const parseString = (hex.startsWith("#")) ? hex.slice(1, 7) : hex;
	if (parseString.length !== 6) {
		return null;
	}
	const r = parseInt(parseString.slice(0, 2), 16);
	const g = parseInt(parseString.slice(2, 4), 16);
	const b = parseInt(parseString.slice(4, 6), 16);
	if (isNaN(r) || isNaN(g) || isNaN(b)) {
		return null;
	}
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const formRedirect = function({ url, method = "POST", data = {} }) {
	const form = document.createElement("form");
	document.body.appendChild(form);
	form.method = method;
	form.action = url;

	Object.keys(data).forEach(key => {
		const input = document.createElement("input");
		input.type = "hidden";
		input.name = key;
		input.value = data[key];
		form.appendChild(input);
	});

	form.submit();
};

export const getStaticPath = function() {
	const staticPrefixFolder = tenantConfig.useGlobalTemplateImagePath ? appConfig.globalTemplateName : `tid${appConfig.tenantId}`;

	const path = `static/${staticPrefixFolder}/`;

	return appConfig.mobile.isMobileApp() ? path : `/${path}`;
};

export const getAwsStaticPath = function() {
	const staticPrefixFolder = tenantConfig.useGlobalTemplateImagePath ? appConfig.globalTemplateName : appConfig.isBM100() ? `${tenantConfig.businessModelId}` : `tid${appConfig.tenantId}`;
	const path = `${store.getters.imageCDNRoot}/static/${staticPrefixFolder}/`;
	return path;
};

export const forEachAsync = async function(array, callback) {
	for (let index = 0; index < array.length; index++) {
		await callback(array[index], index, array);
	}
};

const computeFolderName = (size) => {
	const channel = appConfig.channelAlias();
	switch (channel) {
	case "kiosk":
		return "kiosk";
	default:
		return size;
	}
};

export const mainProductImage = function(product, options = {}) {
	const { width, height } = options;
	const size = (product.UIHint && product.UIHint.fullHeightImage ? "8T7" : "8T5");
	const index = product.options.imageIndexForS1 !== undefined ? product.options.imageIndexForS1 : 0;
	const img = product.imageLinks[index];
	const folderName = img.includes(size) ? "" : computeFolderName(size);
	return imageLoaderNew({
		folderName,
		imageLink: img,
		object: product,
		type: "dynamic",
		width,
		height
	});
};

export const partialString = function(string, limit = 10) {
	if (typeof string === "string" && string.length > limit) {
		return string.substring(0, limit) + "...";
	} else {
		return string;
	}
};

export const isPassedPickupTime = function(pickupDate, pickupTime, timeZone) {
	// checks if pickup date and time is today or in past
	const now = timeBox.now();
	const pickupDateTime = timeBox
		.fromDateTimeString(pickupDate, pickupTime, timeZone)
		.set({
			hour: now.hour().exec(),
			minute: now.minute().exec()
		});

	return now.gt(pickupDateTime).exec();
};

const getEventName = (eventName) => {
	return eventName.includes(" ") ? eventName.split(" ").join("_") : eventName;
};

export const pushGTMInternal = async function(event, path_name, url, page_type, additionalData = {}) {
	const deliveryMethod = store.getters.tenantSpecificData.deliveryChoice
		? getDeliveryMethodByNumberValue(store.getters.tenantSpecificData.deliveryChoice) :
		undefined;
	if (appConfig.mobile.isMobileApp()) {
		await FirebaseAnalytics.logEvent({
			name: getEventName(event),
			params: {
				path_name,
				page_location: url,
				page_type,
				method: additionalData.method,
				user_id: additionalData.email && window.btoa ? window.btoa(additionalData.email) : undefined,
				selected_restaurant: additionalData.selectedRestaurant,
				chosen_delivery_method: deliveryMethod
			}
		});
	}
	appConfig.googleTagManagerDataLayer.push({
		event: event,
		path_name: path_name,
		page_location: url,
		page_type: page_type,
		user_email: additionalData.email,
		method: additionalData.method,
		user_id: additionalData.email && window.btoa ? window.btoa(additionalData.email) : undefined,
		selected_restaurant: additionalData.selectedRestaurant,
		chosen_delivery_method: deliveryMethod
	});
};

export const pushGTMExternal = async function(event, path_name, url) {
	if (appConfig.mobile.isMobileApp()) {
		await FirebaseAnalytics.logEvent({
			name: getEventName(event),
			params: {
				path_name,
				url
			}
		});
	}
	appConfig.googleTagManagerDataLayer.push({
		"event": event,
		"path_name": path_name,
		"url": url
	});
};

export const setConsents = function(consents) {
	window.dataLayer = window.dataLayer || [];
	function gtag(){ window.dataLayer.push(arguments); }
	if(!consents) {
		// Set default consent to 'denied' as a placeholder
		gtag("consent", "default", {
			"ad_storage": "denied",
			"ad_user_data": "denied",
			"ad_personalization": "denied",
			"analytics_storage": "denied"
		});
	} else {
		const marketing = consents["marketing"];
		const statistical = consents["statistical"];
		gtag("consent", "update", {
			"ad_storage": marketing ? "granted" : "denied",
			"ad_user_data":  marketing ? "granted" : "denied",
			"ad_personalization":  marketing ? "granted" : "denied",
			"analytics_storage":  statistical ? "granted" : "denied"
		});
	}
};

export const removeVatFromPrice = (price, vat=0.2, decimalSpaces=2) =>{
	return price-(price*vat).toFixed(decimalSpaces);
};

export const pushGTMeCommerceDataSG = async function({
	eventName, products, category, value, currency, order,
	list = undefined, listOfCategories = undefined, isVatIncluded = true
}) {
	if (products.filter(Boolean).length < 1) return;

	appConfig.googleTagManagerDataLayer.push({ ecommerce: null });

	function getItemCategory(item, category, productId = undefined) {
		if (item.category) {
			return item.category;
		}

		if (category === "findByProductId" && listOfCategories){
			const correctCategory = listOfCategories.find(category => category.productId === productId);
			if(correctCategory && correctCategory.category.length > 0){
				return correctCategory.category[0].category.name;
			}
			return "";
		}

		return category ? category.name : "";
	}

	function getProductQuantity(item) {
		if (item.quantity) {
			return item.quantity;
		}
		if (item.product && item.product.quantity) {
			return item.product.quantity;
		}
		return 1;
	}

	const eventsToShowIndex = ["view_item_list", "select_item"];

	const getCorrectProductIndex = (currentIndex) => {
		if (!eventsToShowIndex.includes(eventName)) {
			return undefined;
		}
		return currentIndex + 1;
	};

	try{
		const dataLayerObject = {
			event: eventName,
			ecommerce: {
				transaction_id: order ? order.orderNumber : undefined,
				currency: currency ? currency : tenantConfig.currency,
				tax: order ? +(value * 0.2).toFixed(2) : undefined,
				value: +(isVatIncluded ? value : removeVatFromPrice(value)).toFixed(2),
				item_list_name: list,
				items: _.flatten(products).map((item) => {
					const getMonetaryDiscountValue = () => {
						if (item.product.discount) {
							if (item.product.discount.discountType === "percentDiscount"){
								const discount = item.product.discount.discountValue / 100;
								return (removeVatFromPrice(getPrice(item)) * discount).toFixed(2) * getProductQuantity(item);
							}
							else if (item.product.discount.discountType === "simpleDiscountFixedAmount"){
								return item.product.discount.discountValue;
							}
						}
						return 0;
					};
					if (item.product) {
						const productData = {
							item_id: item.product.id,
							item_name: item.product.name,
							affiliation: "Online Store",
							index: getCorrectProductIndex(item.product.index),
							// if we cannot provide category, an empty string is sent
							item_category: getItemCategory(item, category, item.product.id),
							price: isVatIncluded ? getPrice(item) : removeVatFromPrice(getPrice(item)),
							quantity: getProductQuantity(item),
							discount: getMonetaryDiscountValue().toFixed(2)
						};

						if (item.product.discount) {
							return {
								...productData,
								coupon: item.product.discount.discountType
							};
						}
						return productData;
					}
				}).filter(Boolean)
			}
		};
		if (appConfig.mobile.isMobileApp()) {
			await FirebaseAnalytics.logEvent({
				name: getEventName(eventName),
				params: {
					...dataLayerObject.ecommerce
				}
			});
		}
		appConfig.googleTagManagerDataLayer.push(dataLayerObject);
	} catch (e) {
		// even if pushing to dataLayer fails, we want to continue execution
	}
};
export const pushGTMeCommerceData = async function({eventName, products, category, value, currency, order}) {
	if(products.filter(Boolean).length < 1) return;
	appConfig.googleTagManagerDataLayer.push({ecommerce: null});
	function getItemCategory(item, category) {
		if(item.category) {
			return item.category;
		}
		return category ? category.name : "Unclassified";
	}
	function getProductQuantity(item) {
		if(item.quantity) {
			return item.quantity;
		}
		if(item.product && item.product.quantity) {
			return item.product.quantity;
		}
		return 1;
	}
	try {
		let coupon;
		const dataLayerObject = {
			event: eventName,
			ecommerce: {
				transaction_id: order ? order.orderNumber : undefined,
				affiliation: "Online Store",
				currency: currency ? currency : tenantConfig.currency,
				tax: order ? +(value * 0.2).toFixed(2) : undefined,
				value: +(value).toFixed(2),
				items: _.flatten(products).map((item) => {
					if(item.product) {
						const productData = {
							item_id: item.product.id,
							item_name: item.product.name,
							currency: currency ? currency : tenantConfig.currency,
							// if we cannot provide category, Unclassified is sent
							item_category: getItemCategory(item, category),
							item_category2: item.product.item_category2,
							price: getPrice(item),
							quantity: getProductQuantity(item),
							discount: item.product.discount ? item.product.discount.discountValue : 0
						};
						if(item.product.discount) {
							coupon = item.product.discount.discountType;
							return {
								...productData,
								coupon: item.product.discount.discountType
							};
						}
						return productData;
					}
				}).filter(Boolean),
				coupon
			}
		};
		if (appConfig.mobile.isMobileApp()) {
			await FirebaseAnalytics.logEvent({
				name: getEventName(eventName),
				params: {
					...dataLayerObject.ecommerce
				}
			});
		}

		appConfig.googleTagManagerDataLayer.push(dataLayerObject);
	} catch (e) {
		// even if pushing to dataLayer fails, we want to continue execution
	}
};

export const getGTMFlattenMultiQuantityProductSelection = (mainProducts, productSelections = []) => {
	const products = [];
	// make a copy of product selections, it will be mutated in the later stage
	const productSelectionsCopy = copyObject(productSelections);
	try {
		mainProducts.forEach((mainProduct, index) => {
			products.push({
				product: {
					id: mainProduct.productId,
					price: mainProduct.price,
					name: mainProduct.name.replace(/\d+\sx\s/,""),
					quantity: mainProduct.quantity || 1
				}
			});
			const productGroups = productSelectionsCopy[index] && productSelectionsCopy[index].groups;
			mainProduct.description.forEach((item) => {
				if(item.text && item.text.includes("(+")) {
					const match = item.text.match(/\(([\d.,+-]+)\)/);
					// check in which group from product selection current product exists
					// when found, we will use product group type as item_category2
					const group = findGroupContainingProduct(productGroups, item.productId);
					if(group) {
						// filter out product when found in a group from selected products, so that next iteration doesn't
						// pick up the wrong one (if the same product is contained in multiple product groups)
						group.selectedProducts = filterSelectedProductsFromGroup(group, item.productId);
					}
					products.push({
						product: {
							id: item.productId,
							price: (match ? parseFloat(match[1].replace(",", ".")) : item.price) / (item.quantity || 1),
							name: item.textRaw,
							quantity: item.quantity,
							item_category2: group && group.options.groupType
						}
					});
				}
			});
		});
	} catch (e) {
		//
	}
	return products;
};

export const getGTMFlattenSingleProductSelection = (mainProduct, quantity = 1, productGroups = []) => {
	const products = [];
	const productGroupsCopy = copyObject(productGroups);
	try {
		products.push({
			product: {
				id: mainProduct.productId,
				price: mainProduct.price,
				name: mainProduct.name.replace(/\d+\sx\s/,""),
				quantity: quantity
			}
		});
		mainProduct.description.forEach((item) => {
			if(item.text && item.text.includes("(+")) {
				const match = item.text.match(/\(([\d.,+-]+)\)/);
				const group = findGroupContainingProduct(productGroupsCopy, item.productId);
				if(group) {
					group.selectedProducts = filterSelectedProductsFromGroup(group, item.productId);
				}
				products.push({
					product: {
						id: item.productId,
						price: (match ? parseFloat(match[1].replace(",", ".")) : item.price) / (item.quantity || 1),
						name: item.textRaw,
						quantity: item.textRaw === "Pant" ? item.quantity * quantity : item.quantity,
						item_category2: group && group.options.description
					}
				});
			}
		});
	} catch (e) {
		//
	}
	return products;
};

export const findGroupContainingProduct = (groups, productId) => {
	return (groups || []).find(group => {
		return (group.selectedProducts || []).find(p => {
			if(typeof p === "string") {
				return p === productId;
			}
			return p.productId === productId;
		});
	});
};

export const filterSelectedProductsFromGroup = (group, productId) => {
	return group.selectedProducts.filter(p => {
		if(typeof p === "string") {
			return p !== productId;
		}
		return p.productId !== productId;
	});
};

export const getGTMTotalPrice = (products) => {
	let total = 0;
	for(let i = 0; i < products.length; i++) {
		total += products[i].product.price * (products[i].product.quantity || 1);
	}
	return total;
};

export const generateGTMList = function (firstPage, dropdownMenu, categoryName, productName) {
	const formattedFirstPage = firstPage.toLowerCase().replace(/\s+/g, "_");
	const formattedDropdownMenu = dropdownMenu ? dropdownMenu.map(category => category.toLowerCase().replace(/\s+/g, "_")) : "";
	const formattedCategoryName = categoryName ? categoryName.toLowerCase().replace(/\s+/g, "_") : "";
	const formattedProductName = productName ? productName.toLowerCase().replace(/\s+/g, "_") : "";

	if (formattedFirstPage === formattedCategoryName) {
		return formattedFirstPage + (formattedProductName ? ` > ${formattedProductName}` : "");
	}
	const listItems = [];
	listItems.push(formattedFirstPage);
	formattedDropdownMenu && formattedDropdownMenu.forEach(item=>{
		listItems.push(item);
	});
	listItems.push(formattedCategoryName);
	listItems.push(formattedProductName);
	return listItems.filter(a=> a.length).join(" > ");
};

export const prepareGTMeCommerceData = function (rl = null) {
	const receiptLines = rl || JSON.parse(localStorage.getItem("gtmReceiptLines"));
	const productArr = [];
	let cartTotal = 0;
	try {
		receiptLines.forEach((receiptLine) => {
			const quantity = receiptLine.quantity ? receiptLine.quantity : 1;
			const prodUnitPrice = parseFloat(receiptLine.totalPriceAccum) ? parseFloat(receiptLine.totalPriceAccum) : parseFloat(receiptLine.totalPrice);
			const pantValue = getPantValue(receiptLine, quantity);
			const discount = receiptLine.discount;
			productArr.push({
				product: {
					id: receiptLine.productId,
					name: receiptLine.name ? receiptLine.name : receiptLine.text,
					quantity: quantity,
					discount: discount,
					price: receiptLine.price
				},
				productPrice: {
					basePrice: (prodUnitPrice - pantValue) / quantity
				},
				category: receiptLine.category ? receiptLine.category : ""
			});
			cartTotal += parseFloat(prodUnitPrice - pantValue);
		});
		return {items: productArr, value: cartTotal};
	} catch (e) {
		// even if pushing to dataLayer fails, we want to continue execution
		return {items: [], value: 0};
	}
};

export const getProductCategory = function (categories, item) {
	return categories.find((category) => {
		return category && category.elements[0]
			? category.elements[0].props.products.includes(item.product.id)
			: undefined;
	});
};

export const remapGTMReceiptLines = function (receiptLines, orderLines) {
	// we use these remapped items in different states for cart and orders
	// since they hold the most necessary data for GTM
	return JSON.stringify(receiptLines.map((rl) => {
		const ol = orderLines.find((ol) => {
			return rl.productId === ol.productId;
		});

		if (!ol) {
			return undefined;
		}

		let discount;
		const voucherDiscount = ol.subLines.find(sl => sl.type === "Voucher");
		const appliedVoucherId = voucherDiscount && voucherDiscount.values[0].voucherId;

		if (appliedVoucherId) {
			discount = ol.applicableDiscounts.find(ad => ad.voucherPattern.includes(appliedVoucherId));
		} else {
			discount = rl.priceBefore > rl.price ? ol.discount : null;
		}

		return {
			...rl,
			// in most cases we should use name from orderLine, receiptLine adds quantity and should be used only if ol
			// doesn't exist
			name: ol && ol.name ? ol.name : rl.name,
			category: ol && ol.category
				? ol.category.name
				: ol.options && ol.options.appliedPriceRule && ol.options.appliedPriceRule.options && ol.options.appliedPriceRule.options.label,
			discount: discount
		};
	}).filter(Boolean));
};

export const calculateCategoryTotal = function (items) {
	return _.flatten(items).reduce((acc, curr) => {
		// there are some specific cases for kiosk where non-product items are pushed into category
		// these don't have productPrice and we can use this as a check for the aggregation
		return curr.productPrice ? acc + (curr.productPrice.discountPrice || curr.productPrice.basePrice) : acc + 0;}, 0);
};

export const getPrice = function (item) {
	if(item.productPrice) {
		return item.productPrice.discountPrice ? item.productPrice.discountPrice : item.productPrice.basePrice;
	}
	return item.product.price;
};

export const getPantValue = function (receiptLine, quantity) {
	const pant = receiptLine.description.find(item => item.textRaw === "Pant");
	if(pant) {
		return pant.price * quantity;
	}
	return 0;
};

export const getIdAndNameFromCategory = function (category) {
	if(category) {
		return {
			id: category.id,
			name: category.name
		};
	}
	return undefined;
};

export const sendFbPixel = function(event, value, contentIds) {
	return appConfig.fbPixel("track", event, {
		currency: tenantConfig.currency,
		value,
		content_ids: contentIds,
		content_type: "product"
	});
};

export const findOrCreateProductSelection = function(id, productCatalog, component) {
	if (component.$store.getters.cartProducts) {
		const productSelections = component.$store.getters.selections.filter(p => {
			return p.productId === id;
		});

		const psOptions = {
			expiration: {
				tenantSettings: component.$store.getters.tenantSettings.productSelectionExpiration,
				tenantConfig: tenantConfig.productSelectionExpiration
			}
		};
		if (productSelections) {
			return copyObject(productSelections);
		} else {
			return [ProductSelection.createFromProduct(productCatalog.getProduct(id, true), psOptions)];
		}
	}
};
/**
 * takes objType === "GridBlockElement" element and transforms it to item
 * which is product with additional helper functions and properties
 * @param {Array<Product>} products which is actually element.items
 */
export const transformProductsToItems = function(products, productCatalog, component, deliveryChoice) {
	const self = this;

	const generalProducts = products.map(product => {
		const productSelections = findOrCreateProductSelection(product.id, productCatalog, component);

		return {
			product,
			productPrice: getProductPrice(product, productCatalog),
			quantity: productSelections.reduce((carry, selection) => carry + selection.quantity, 0),
			productSelections,
			cartQuantityDialogShown: false,
			timeoutHandle: window.setTimeout(() => {}, 3000),
			isProductEnabled: (() => {
				// check if product is disabled
				if (!product.isActive) {
					return false;
				}

				// check if one of its single subproducts is disabled
				const singleSubProducts = product.groups
					.filter(g => g.appearance === "single")
					.map(group => productCatalog.getProduct(group.options.defaultSelected, true));
				if (singleSubProducts.some(supProduct => !supProduct.isActive)) {
					return false;
				}
				if (Array.isArray(product.assortmentRules) && product.assortmentRules.length && self.globalSelectedShop) {
					const forbiddenShop = product.assortmentRules.find(rule => rule.shopId === self.globalSelectedShop.id);
					return forbiddenShop === undefined;
				}

				return true;
			})(),
			hasGroupsOrConfigurations: (() => {
				if (product.departmentId === 2) {
					const singleSubProducts = product.groups
						.filter(g => g.appearance === "single")
						.map(group => productCatalog.getProduct(group.options.defaultSelected, true));
					return singleSubProducts.some(supProduct => supProduct.groups.length > 0 || supProduct.configurations.length > 0);
				} else {
					return product.groups.length > 0 || product.configurations.length > 0;
				}
			})()
		};
	});
	const fileteredProducts = generalProducts
		.filter(product => {
			if (!product.product.isActive) {
				return false;
			}
			if (!product.isProductEnabled) {
				return false;
			}
			const options = product.product.options;
			if (options.deliveryMethodIndex) {
				if (!options.deliveryMethodIndex.includes(deliveryChoice)) {
					return false;
				}
			}
			if (!isProductAvailableForChannel(product.product)) {
				return false;
			}
			return true;
		});
	return fileteredProducts;
};
export const refreshProducts = function(category, productCatalog, component) {

	if (category.elements) {
		const productIds = _.flattenDeep([...category.elements.map(element => element.props).map(prop => prop.products)]);
		const generalProducts = productIds.map(id => {
			const productSelections = findOrCreateProductSelection(id, productCatalog, component);

			const product = productCatalog.getProduct(id, true);

			return {
				product,
				productPrice: getProductPrice(product, productCatalog),
				quantity: productSelections.reduce((carry, selection) => carry + selection.quantity, 0),
				productSelections,
				cartQuantityDialogShown: false,
				timeoutHandle: window.setTimeout(() => {}, 3000),
				isProductEnabled: (() => {
					// check if product is disabled
					if (!product.isActive) {
						return false;
					}

					// check if one of its single subproducts is disabled
					const singleSubProducts = product.groups
						.filter(g => g.appearance === "single")
						.map(group => productCatalog.getProduct(group.options.defaultSelected, true));

					return !singleSubProducts.some(supProduct => !supProduct.isActive);


				})(),
				hasGroupsOrConfigurations: (() => {
					if (product.departmentId === 2) {
						const singleSubProducts = product.groups
							.filter(g => g.appearance === "single")
							.map(group => productCatalog.getProduct(group.options.defaultSelected, true));
						return singleSubProducts.some(supProduct => supProduct.groups.length > 0 || supProduct.configurations.length > 0);
					} else {
						return product.groups.length > 0 || product.configurations.length > 0;
					}
				})()
			};
		});
		return Object.values(generalProducts.filter(product => product.product.isActive));
	}
};
const getFilteredProductGroupSteps = (productGroupsWithoutBaseProduct, product) => {
	// generate config steps based on the existing product groups for the "extras" template
	const stepsBasedOnRealGroups = productGroupsWithoutBaseProduct.map(() => CONFIGURATION_STATE_TYPE.EXTRAS);
	// if both fries and dips are present under a composite product
	// they are merged into one "EXTRAS" step
	if(product.options.mergeDipsWithFries) {
		const dipsGroupIndex = productGroupsWithoutBaseProduct.findIndex(g => {
			return g.options && g.options.groupType === "dips";
		});
		const friesGroupIndex = productGroupsWithoutBaseProduct.findIndex(g => {
			return g.options && g.options.groupType === "fries";
		});
		if (dipsGroupIndex > -1 && friesGroupIndex > -1) {
			return stepsBasedOnRealGroups.filter((_, i) => i !== dipsGroupIndex);
		}
	}
	return stepsBasedOnRealGroups;
};
export const generateProductConfigSteps = (item) => {
	const product = item.product ? item.product : item;
	// we slice 1st group, since that is the main product
	// and on that position will potentially be the GROUPS_CONFIGS step
	const productGroupsWithoutBaseProduct = product.groups.slice(1).filter(group => {
		return group.options.showToCustomer !== false;
	});
	const stepsBasedOnRealGroups = getFilteredProductGroupSteps(productGroupsWithoutBaseProduct, product);
	return product.options.showGroupConfigs !== false
		? [CONFIGURATION_STATE_TYPE.GROUPS_CONFIGS, ...stepsBasedOnRealGroups]
		: stepsBasedOnRealGroups;
};
export const CONFIGURATION_STATE_TYPE = Object.freeze({
	SIZE: "SIZE",
	GROUPS_CONFIGS: "GROUPS_CONFIGS",
	DRINKS: "DRINKS",
	EXTRAS: "EXTRAS"
});

export const ORDER_STEPS = Object.freeze({
	1: "USER_DATA",
	2: "PICKUP_TIME",
	3: "PENDING_ORDER"
});

export const LOGIN_STEPS = Object.freeze({
	1: "LOGIN",
	2: "SIGNUP"
});

export const resetConfigurationStep = function(component) {
	component.$store.commit(mutationTypes.RESET_CONFIGURATION_STEP, { resetValue: 0 });
};

export const getFirstElementOfArray = function(array) {
	return array.find(x => x !== undefined);
};

export const globalTemplateName = appConfig.globalTemplateName;
export const channel = appConfig.channelAlias();
export const getCategoriesByTemplate = function({ productCatalog, onlyActive = true, sort = true }) {
	const categories = [...productCatalog.getCategories()].filter(cat => cat.isActive === onlyActive && cat.globalTemplate === appConfig.globalTemplateName);
	if (sort) {
		return categories.sort((p1, p2) => {
			return parseInt(p1.menuPosition) - parseInt(p2.menuPosition);
		});
	} else {
		return categories;
	}
};

/**
 *	Returns category object based on category ID
 *
 * @param {String} catId category ID
 * @param {ProductCatalog} productCatalog object
 * @returns {Category || undefined} promise that can successfully evaluate to appropriate category object
 */
export const getCategory = function(catId, productCatalog) {
	const categories = [...productCatalog.getCategories()]
		.filter(cat => cat.isActive === true && cat.globalTemplate === globalTemplateName)
		.sort((p1, p2) => parseInt(p1.menuPosition) - parseInt(p2.menuPosition));
	return categories.find(c => c.extId === parseInt(catId));
};
export const getCategoryById = function(catId, productCatalog) {
	const categories = [...productCatalog.getCategories()];
	return categories.find(c => c.extId === parseInt(catId));
};
export const getCategoriesByShopId = (shopId, productCatalog) => {
	const customerType = evaluateCustomerType();
	const categories = productCatalog.getCategories()
		.filter(cat => {
			const availableToCustomerType = !cat.customerTypes
				|| !cat.customerTypes.length
				|| cat.customerTypes.includes(customerType);
			if(cat.isSpecificForShops && cat.channels){
				if (Array.isArray(cat.isSpecificForShops)) {
					return cat.isSpecificForShops.includes(shopId)
						&& cat.isActive === true
						&& cat.channels.includes(channel)
						&& availableToCustomerType;
				}
				return cat.isActive === true
					&& cat.channels.includes(channel)
					&& availableToCustomerType;
			}
		})
		.sort((p1, p2) => parseInt(p1.menuPosition) - parseInt(p2.menuPosition));
	return [...categories];
};
//filterAndSelect function can be used on list of checkboxes with search filed( for searching that elements) for SORTING
//it sets selected checkoxes on the top by alphabetical order and elements below also alphabetically
//also while searching selected checkboxes remain on top
export const filterAndSelect = function(inputArray, shopsOrDepartments, propertyName, searchText = "") {
	if (searchText === null) {
		searchText = "";
	}
	if (!Array.isArray(inputArray)) {
		inputArray = [inputArray];
	}
	shopsOrDepartments.sort((l, r) => {
		const firstElLower = l[propertyName].toLowerCase();
		const secondElLower = r[propertyName].toLowerCase();
		const reg = eval("/" + `${searchText.toString().toLowerCase()}` + "/");
		if (inputArray.includes(l.id) && inputArray.includes(r.id)) {
			if (l[propertyName] < r[propertyName]) {
				return -1;
			}
			if (l[propertyName] > r[propertyName]) {
				return 1;
			}
			return 0;
		}
		if (inputArray.includes(l.id) && !inputArray.includes(r.id)) {
			if (l[propertyName] < r[propertyName]) {
				return -1;
			}
			if (l[propertyName] > r[propertyName]) {
				return -1;
			}
			return -1;
		}
		if (!inputArray.includes(l.id) && inputArray.includes(r.id)) {
			if (l[propertyName] < r[propertyName]) {
				return 1;
			}
			if (l[propertyName] > r[propertyName]) {
				return 1;
			}
			return 1;
		}
		if (searchText === "") {
			return l[propertyName].localeCompare(r[propertyName]);
		}
		if (reg.test(firstElLower) && reg.test(secondElLower)) {
			if (firstElLower < secondElLower) {
				return -1;
			}
			if (firstElLower > secondElLower) {
				return 1;
			}
			return 0;
		}
		if (reg.test(firstElLower) && !reg.test(secondElLower)) {
			if (firstElLower < secondElLower) {
				return -1;
			}
			if (firstElLower > secondElLower) {
				return -1;
			}
			return -1;
		}
		if (!reg.test(firstElLower) && reg.test(secondElLower)) {
			if (firstElLower < secondElLower) {
				return 1;
			}
			if (firstElLower > secondElLower) {
				return 1;
			}
			return 1;
		}
	});
};

/**
 * Checks if product can be picked up.
 *
 * @param product
 * @param productCatalog
 * @param pickupDateTimeRules
 * @param shopId
 * @returns {boolean}
 */
export const canBePickedUp = (product, productCatalog, pickupDateTimeRules, shopId) => {

	if (isProductDisabled(product) || isProductDisabledForDeliveryChoice(product) || !isProductAvailableForChannel(product)) {
		return false;
	}

	const boxDate = timeBox.realNow();
	const productSelections = [ProductSelection.createFromProduct(productCatalog.getProduct(product.id, true))];
	// TODO try using pure canBePickedUp!!

	return OrderHelperObject.canBePickedUp({
		boxDate,
		productSelections,
		tenantData: tenantConfig,
		pickupDateTimeRules: pickupDateTimeRules,
		shopId,
		productCatalog,
		hiddenItems: store.getters.aBTestsHiddenItems
	});
};

/**
 * Filter the list of favorite products by globaly selected shopId
 *
 * @param favoriteProducts
 * @param productCatalog
 * @param globalSelectedShopId
 * @param cachedPickupRules
 * @returns
 */
export const filterCurrentUserFavoriteProductListByShopId = (
	favoriteProducts,
	productCatalog,
	globalSelectedShopId,
	cachedPickupRules
) => favoriteProducts.filter((favProd) => {
	try {
		const categoriesOfProduct = productCatalog.getAllCategoriesOfProduct(favProd.productId);
		const isProductCategoriesContainSelectedShop = categoriesOfProduct.some(
			(category) => Array.isArray(category.isSpecificForShops)
				&& category.isSpecificForShops.includes(globalSelectedShopId)
		);
		const isProductSupportedByActiveChannel = !categoriesOfProduct.length || categoriesOfProduct.filter(category => !category.channels || category.channels.includes(appConfig.channelAlias())).length;
		const product = productCatalog.getProduct(favProd.productId);
		return isProductCategoriesContainSelectedShop && isProductSupportedByActiveChannel && canBePickedUp(
			product,
			productCatalog,
			cachedPickupRules,
			globalSelectedShopId
		);
	} catch (e) {
		return undefined;
	}
});

export const evaluateCustomerType = () => {
	if(store.getters.isSubscribed) {
		return CUSTOMER_TYPES.PLUS_MEMBERS;
	}
	if(store.getters.isSunsetPointsMember) {
		return CUSTOMER_TYPES.POINT_MEMBERS;
	}
	return CUSTOMER_TYPES.GUESTS;
};

export const isProductDisabledForDeliveryChoice = (product) => {
	const deliveryChoice = store.getters.tenantSpecificData.deliveryChoice;
	return deliveryChoice !== undefined && product.options.deliveryMethodIndex ? !product.options.deliveryMethodIndex.includes(deliveryChoice) : false;
};

export const isProductAvailableForChannel = (product) => {
	const options = product.options;
	if (!options) {
		return true;
	}
	const channelToggles = options.channelTogglesState;
	if (channelToggles && !channelToggles.includes(appConfig.channelAlias())) {
		return false;
	}
	return true;
};

export const isProductDisabled = (product) => {
	return product.id === undefined || product.isActive === false;
};

export const isProductAllowedToProceed = ({ product }) => {
	if (!product.isActive) {
		return false;
	}

	const deliveryChoice = store.getters.tenantSpecificData.deliveryChoice;
	return product.groups.every(group => group.products.some(product => {
		const options = product.options;
		if (!options) {
			return true;
		}
		if (options.deliveryMethodIndex) {
			if (!options.deliveryMethodIndex.includes(deliveryChoice)){
				return false;
			}
		}
		if (!isProductAvailableForChannel(product)) {
			return false;
		}
		return true;
	}));
};

export const removeUnavailableProducts = (group, productCatalog, pickupRules, shopId, deliveryOption) => {
	// TODO try using pure canBePickedUp!!
	const newProducts = group.products.filter(p => {
		const canProductBePickedUp = canBePickedUp(p, productCatalog, pickupRules, shopId);
		if (!canProductBePickedUp) {
			return canProductBePickedUp;
		}

		if(p.options && p.options.deliveryMethodIndex) {
			const isProductAllowed = p.options.deliveryMethodIndex.includes(deliveryOption);
			if(!isProductAllowed){
				return isProductAllowed;
			}
		}

		if (!isProductAvailableForChannel(p)) {
			return false;
		}

		const isNoThanks = isNoThanksProduct(p);
		if (!isNoThanks) {
			return !isNoThanks;
		}

		return appConfig.isBM100();
	});
	return remapDefaultSelectedProduct({...group, products: newProducts});
};

export const remapProductGroupDefaultSelectedProduct = (productGroups, deliveryOption) => {
	const newProductGroups = productGroups.map(group => {
		const newProducts = group.products.filter(p => {
			if(p.options && p.options.deliveryMethodIndex) {
				const isProductAllowed = p.options.deliveryMethodIndex.includes(deliveryOption);
				if(!isProductAllowed){
					return isProductAllowed;
				}
			}
			if (!isProductAvailableForChannel(p)) {
				return false;
			}
			return true;
		});
		if(newProducts){
			return remapDefaultSelectedProduct({...group, products: newProducts});
		}
	});

	const newProductGroupMap = {};
	newProductGroups.forEach(group => {
		newProductGroupMap[group.id] = group;
	});

	return {
		remappedGroups: newProductGroups,
		remappedGroupMap: newProductGroupMap
	};
};

export const remapDefaultSelectedProduct = (group) => {
	if(!group.options.defaultSelected) {
		return group;
	}

	const productsIds = group.products.map(product => {
		return product.id;
	});

	if(productsIds.includes(group.options.defaultSelected)) {
		return group;
	}

	/* it can happen that products are disabled by pickup rules
	 thus, we should check if group actually contains products
	 */
	const groupOptions = {
		...group.options,
		defaultSelected: group.products.length > 0
			? group.products[0].id
			: undefined
	};
	return {...group, options: groupOptions};
};

export const traverseArray = (array) => array
	.map((data) => {
		if (data instanceof Array) {
			return traverseArray(data);
		}

		if (typeof data === "object") {
			return traverseObject(data);
		}

		return data;
	})
	.flat();

export const traverseObject = (object) => Object
	.entries(object)
	.map(([objectKey, objectData]) => {
		if (objectData instanceof Array) {
			return { option: objectKey, value: traverseArray(objectData) };
		}
		if (typeof objectData === "object" && objectData) {
			return traverseObject(objectData);
		}
		return { option: objectKey, value: objectData };
	})
	.flat();

export const flowSetting = {
	set: (flowName) => sessionStorage.setItem("routesFlow", flowName),
	get: () => sessionStorage.getItem("routesFlow"),
	remove: () => sessionStorage.removeItem("routesFlow"),
	isFlow: (flowName) => sessionStorage.getItem("routesFlow") === flowName
};

export const getUserTimeZone = () => {
	return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const productImageCache = {};
export const getBackImages = (product, shouldBeKiosk = false, appendDoubleQuotes = false) => {
	const cacheKey = `${product.id}-${shouldBeKiosk}-${appendDoubleQuotes}`;

	// if (!productImageCache[cacheKey]) {
	const defaultImageRatio = {
		[appConfig.businessModelsEnum.bm100.id]: "1T1",
		default: "8T5"
	};
	const lowerFolderValueRatio = Object.values(defaultImageRatio);
	const channel = appConfig.channelAlias();

	// @TODO check is this same as getImageFolder
	const splitFolderFromImage = (img) => {
		const imagePathSplit = img.split("/");
		if (imagePathSplit.length === 1) {
			const imageRatio = defaultImageRatio[tenantConfig.tenantId] || defaultImageRatio.default;
			return [channel === "kiosk" && shouldBeKiosk ? "kiosk" : imageRatio, img];
		}
		return imagePathSplit;
	};

	productImageCache[cacheKey] = product.imageLinks
		.slice()
		.sort((img1, img2) => {
			return [img1, img2]
				.map(splitFolderFromImage)
				.map(([directory]) => {
					if (lowerFolderValueRatio.includes(directory) === shouldBeKiosk) {
						return 1;
					}

					return 0;
				})
				.reduce((prev, curr) => prev - curr);
		})
		.map(img => {
			if (channel !== "kiosk" || !shouldBeKiosk) {
				const lastFullStop = img.lastIndexOf(".");
				return img.substring(0, lastFullStop) + "_thumb" + img.substring(lastFullStop);
			}
			return img;
		})
		.map(imgURL => imageLoader(...splitFolderFromImage(imgURL), product, "dynamic", true, true))
		.map((imgURL) => appendDoubleQuotes ? `"${imgURL}"` : imgURL);
	// }

	return productImageCache[cacheKey];
};

export const prepareOrderIngredientsForPrint = orders => {
	// first lets get all products with ingredients from all the orders
	const ordersProducts = orders.map(order => {
		const getProductsFromLines = (orderLines, parentQuantity) => {
			const products = orderLines
				.filter(orderLine => Array.isArray(orderLine.productIngredients) && orderLine.productIngredients.length > 0)
				.map(orderLine => {
					const realQuantity = orderLine.quantity ? orderLine.quantity : parentQuantity;

					return {
						name: orderLine.name,
						productId: orderLine.productId,
						productIngredients: orderLine.productIngredients,
						quantity: realQuantity,
						shouldBeHeated: order.orderOptions.mustBeHeated
					};
				});

			const subProducts = orderLines.reduce((subProducts, orderLine) => {
				if (Array.isArray(orderLine.subLines)) {
					const realQuantity = orderLine.quantity ? orderLine.quantity : parentQuantity;
					return [...subProducts, ...getProductsFromLines(orderLine.subLines, realQuantity)];
				} else {
					return subProducts;
				}
			}, []);

			return [...products, ...subProducts];
		};

		return {
			orderNumber: order.orderNumber,
			products: getProductsFromLines(order.orderLinesWithIngredients)
		};
	});

	return ordersProducts.reduce((categoryProducts, orderProducts) => {
		const addProductToCategory = (product) => {
			const categoryId = _.get(product.productIngredients[0].ingredient, "foodCategory.id") || -1;
			const categoryName = _.get(product.productIngredients[0].ingredient, "foodCategory.name") || "";

			if (typeof categoryProducts[categoryId] === "undefined") {
				categoryProducts[categoryId] = {
					productOverviews: {},
					categoryName: categoryName
				};
			}

			if (typeof categoryProducts[categoryId].productOverviews[product.productId] === "undefined") {
				const productIngredients = product.productIngredients.map(el => {
					const { ingredient, ...product } = el;
					const { unit, ...rest}  = ingredient;
					return ({
						...product,
						ingredient: {
							...rest,
							unit: unit === "n\\a" ? "" : unit
						}
					});
				});

				categoryProducts[categoryId].productOverviews[product.productId] = {
					sumQuantity: 0,
					breakDownQuantity: [],
					breakDownQuantityWithWarmLabel: [],
					productName: product.name,
					productIngredients: productIngredients,
					hasHeatedProduct: false,
					ingredientAmount: productIngredients.length === 1 ? productIngredients[0].amount : 0,
					ingredientUnit: productIngredients.length === 1 ? productIngredients[0].ingredient.unit : ""
				};
			}

			categoryProducts[categoryId].productOverviews[product.productId].sumQuantity += product.quantity;
			categoryProducts[categoryId].productOverviews[product.productId].breakDownQuantity.push(product.quantity);

			if (product.shouldBeHeated) {
				categoryProducts[categoryId].productOverviews[product.productId].hasHeatedProduct = true;
			}

			const warmLabelQuantity = product.shouldBeHeated ? `${product.quantity} V` : product.quantity.toString();
			categoryProducts[categoryId].productOverviews[product.productId].breakDownQuantityWithWarmLabel.push(warmLabelQuantity);
		};

		orderProducts.products.forEach(product => {
			addProductToCategory(product);
		});

		return categoryProducts;
	}, {});
};
export const transientReleaseToggles = {
	...appConfig.transientReleaseToggles
};

export const delay = (time, value = null, rejectDelay = false) => {
	return new Promise(function(resolve, reject) {
		const callback = rejectDelay === true ? reject : resolve;
		setTimeout(callback.bind(null, value), time);
	});
};

export const mapObject = (fromObject, toObject, props) => {
	props.forEach((prop) => {
		toObject[prop] = fromObject[prop] ? fromObject[prop] : toObject[prop];
	});
	return toObject;
};
export const getUrlParameter = (name, url) => {
	// if url is not specified, we use the current url as the default url
	if (url === undefined) {
		url = window.location.href;
	}
	name = name.replace(/[[]/, "\\[").replace(/[\]]/, "\\]");
	const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
	const results = regex.exec(url);
	return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
};

export const deleteItemsFromLocalStorage = (items) => {
	items.forEach(item => {
		localStorage.removeItem(item);
	});
};

export const toLocaleDate = (date, locale) => {
	return timeBox.fromISO(date).setLocale(locale || appConfig.tenantConfig.locale).format("d. LLLL yyyy").exec();
};

export const pickCategory = ( selectedShopId, catalog, getIconUrlFunction, router) => {

	let preselectedCategory = -1;
	let categories = getCategoriesByShopId(selectedShopId, catalog);
	const LOCAL_STORAGE_KEY = "scrollToProductIds";
	const preselectedProductIds = localStorage.getItem(LOCAL_STORAGE_KEY);
	if(preselectedProductIds) {
		const queryProductsIds = preselectedProductIds.split(",");
		const productId = queryProductsIds.find(productId => {
			return categories.find(category => {
				if(category.elements[0].props.products.includes(productId)){
					preselectedCategory = category;
					return category;
				}
			});
		});
		if(preselectedCategory !== -1){
			localStorage.removeItem(LOCAL_STORAGE_KEY);
			localStorage.setItem(LOCAL_STORAGE_KEY, productId);
			categories = categories.map(newCategory => {
				newCategory.isSelected = newCategory.id === preselectedCategory.id;
				newCategory.fullIconUrl = getIconUrlFunction(newCategory);
				return newCategory;
			});
			router.push(`/category/${preselectedCategory.extId}`);
		}
	}
	if (categories[0] && preselectedCategory === -1) {
		router.push({
			name: "defaultProductCatalog",
			params: {
				categoryId: categories[0].extId.toString()
			}
		});
	}
};
// if vat is included just do not supply vatRatesMgr
export const getSubscriptionPrice = async (subscriptionProvider, productPlanId, vatRatesMgr = undefined, productPlanChargeIdx = 0) => {
	const produtPlanDetails = await subscriptionProvider.getSubscriptionPlanById(productPlanId);
	const productPlanChargePrice = produtPlanDetails.productPlanCharges[productPlanChargeIdx].productPlanChargePricing[productPlanChargeIdx].price;
	if(vatRatesMgr){
		const vatRate = vatRatesMgr.getVatRate();
		const totalVat = vatRatesMgr.calculateTotalVat(productPlanChargePrice, vatRate.vatRate, false);
		return formatPriceZero(productPlanChargePrice+totalVat);
	}

	return formatPriceZero(productPlanChargePrice);
};

export const afterSuccessfulLoginHelper = async (featureToggles, store, showFromDifference = 1)=> {
	if(featureToggles.fraudProtection){
		const {activeSessions, maximumDevices} = await store.dispatch(mutationTypes.MAXIMUM_NUMBER_OF_DEVICES_REACHING);
		if(maximumDevices - Object.keys(activeSessions).length <= showFromDifference){
			store.commit(mutationTypes.SET_FRAUD_PROTECTION, {
				modalVisible: true,
				activeSessionsNumber: Object.keys(activeSessions).length,
				maximumDevicesNumber: maximumDevices,
				messageType: "maximumNumberOfDevicesReaching",
				activeSessions: activeSessions
			});
		}
	}
};

export const activeDevices = {
	getArray(activeSessions, reducer, t){
		return Object.keys(activeSessions).reduce((acc, curr) => {
			return reducer(activeSessions, acc, curr, t);
		}, [
		]);
	},
	formatForFraudProtectionDialog(activeSessions, acc, curr, t){
		const {device} = activeSessions[curr];
		if(!device) {
			LoggerProvider.logEvent({
				message: `Error: device is undefined: ${curr}: ${activeSessions[curr]}`,
				eventName: `Fraud protection device tracking unsuccessful`,
				eventCategory: "FraudProtection",
				eventDomain: "FraudProtectionDeviceTracking",
				data: {
					deviceData: {
						[curr]: activeSessions[curr]
					}
				}
			});
		}
		if(device && device !== "unknown"){
			const model = activeDevices.safeProp(device.model);
			const browser = activeDevices.safeProp(device.browser);
			const platform = activeDevices.safeProp(device.platform);
			const version = activeDevices.safeProp(device.version);
			const delimiter = ",";

			return [
				...acc,
				`${activeDevices.formatProp(model, browser)}${activeDevices.formatProp(browser, "")}${activeDevices.formatProp(delimiter, model || browser)}${activeDevices.formatProp(platform, version)}${activeDevices.formatProp(version, "")}`
			];
		}
		return [
			...acc,
			t("utilityComponents.fraudProtection.unknownDevice")
		];
	},
	// prop value could be "unknown" so we are formatting it to proper string
	safeProp(prop){
		return prop && prop !== "unknown" ? prop : "";
	},
	// if some props is missing trailing space shouldn't exist, for example when browser is missing
	// iPhone X , ... should be instead: iPhoneX, ...
	formatProp(prop, dependsOnProp){
		const res = dependsOnProp !== "" ? `${prop} ` : prop;
		return res;
	}
};

export const getSessionKey = (clientTraceId, deviceData) => {
	return deviceData && deviceData.uuid && deviceData.uuid !== "unknown" ? deviceData.uuid : clientTraceId;
};

export const getListTileText = (productCatalog, productSelection, group, objectName, selectedProductId) => {
	const staticRemapping = {
		default: {
		},
		[appConfig.businessModelsEnum.bm100.id]: {
			dip: "common.sideOrdersBm100"
		}
	};

	if (group.options.staticText) {
		const text = group.options.staticText;
		const remappingName = _.get(staticRemapping, `${tenantConfig.businessModelId}.${text.toLowerCase()}`, staticRemapping.default[text.toLowerCase()]);
		return remappingName || text;
	}
	if(selectedProductId){
		const prod = productCatalog.getProduct(selectedProductId);
		return {
			id: prod["id"],
			text: prod[objectName]
		};
	}
};
export const updateTenantSetting = async (settingName, settingValue) => {
	try{
		await store.dispatch("updateServerData", {
			settingName: settingName,
			settingValue: settingValue
		});
	} catch(e){
		return e;
	}
};
export const pushNotificationSubscription = ({topic, unsubscribe = false}) => {
	if (!appConfig.mobile.isMobileApp()) {
		return;
	}
	if (unsubscribe === true) {
		// eslint-disable-next-line no-console
		FCM.unsubscribeFrom({topic}).then(({message}) => console.log("[INFO] User", message));
	} else {
		// eslint-disable-next-line no-console
		FCM.subscribeTo({topic}).then(({message}) => console.log("[INFO] User", message));
	}
};

export const getActiveVersion = (globalVersions) => {
	if (!Array.isArray(globalVersions) || !globalVersions.length) {
		return 1;
	}

	const actvieVersionData = globalVersions.find(i => i.isActive === true);
	if (!actvieVersionData) {
		return 1;
	}

	return actvieVersionData.id;
};

export const subscribeToUniversalLinks = (router, cb) => {
	if (!appConfig.mobile.isMobileApp()) {
		return;
	}

	NativeApp.addListener("appUrlOpen", (event) => {
		if (!event.url) return;
		const url = new URL(event.url);
		const slug = url.pathname;
		const query = {};
		if(url.search) {
			const queryParams = new URLSearchParams(url.search);
			const keys = queryParams.keys();
			for (const key of keys) {
				query[key] = queryParams.get(key);
			}
		}
		cb && cb(event, slug);

		if (slug) {
			router.push({
				path: slug,
				query
			});
		}
	});
};

export const toBase64 = (blob) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(blob);
		reader.onload = () => resolve(reader.result);
		reader.onerror = (error) => reject(error);
	});

export const monthsDayMapper =  (month, year) => {
	switch (Number.parseInt(month)) {
	case 1:
		return 31;
	case 2:
		return Number.parseInt(year) % 4 === 0 ? 29 : 28;
	case 3:
		return 31;
	case 4:
		return 30;
	case 5:
		return 31;
	case 6:
		return 30;
	case 7:
		return 31;
	case 8:
		return 31;
	case 9:
		return 30;
	case 10:
		return 31;
	case 11:
		return 30;
	case 12:
		return 31;
	}
};

export const isDateBeforeToday = (year, month, day) => {
	const today = new Date();
	const pickedDate = new Date(year || 1, month - 1 || 0, day || 1);
	return today >= pickedDate;
};

export const isBirthdateValid = (day, month, year, isBirthdateOptional = false) => {
	const dayValid = day > 0 && day <= 31;
	const monthValid = month > 0 && month <= 12;
	const yearValid = year > 1900 && year <= (new Date()).getFullYear();
	const isBirthdayInputEmpty = !day && !month && !year;
	return (dayValid && monthValid && yearValid) || (isBirthdayInputEmpty && isBirthdateOptional);
};

export const getBaseDomain = (url) => {
	return url.replace(/(^\w+:|^)\/\//, "");
};

export const isDomainMatch = (domainsList, originDomain) => {
	const domains = domainsList.split(";");
	return domains.some(domain => domain.includes(getBaseDomain(originDomain)));
};

/**
 * Helper function to escape regular expression special characters
 */
export const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const addLogoToQRCode = (canvas, imagePath) => {
	if (canvas) {
		const context = canvas.getContext("2d");
		const baseImage = new Image();
		baseImage.src = imagePath;
		baseImage.onload = function () {
			const imageWidth = 50;
			const imageHeight = 50;
			const xOffset = (canvas.width - imageWidth) / 2;
			const yOffset = (canvas.height - imageHeight) / 2;
			context.drawImage(baseImage, xOffset, yOffset, imageWidth, imageHeight);
		};
	}
};

export const RCMSourceType = Object.freeze({
	CrossSell: "CrossSell",
	Carousel: "Carousel"
});

export const mapFeatureToggles = (featureToggles) => {
	const mappedFeatureToggles = {};
	Object.keys(featureToggles).forEach(key => {
		if (typeof featureToggles[key] === "object") {
			mappedFeatureToggles[key] = featureToggles[key].value;
		} else {
			mappedFeatureToggles[key] = featureToggles[key];
		}
	});
	return mappedFeatureToggles;
};

export const maskEmail = (email) => {
	if (!email) {
		return;
	}
	const parts = email.split("@");
	const firstLetter = parts[0][0];
	const restOfLetters = parts[0].slice(1);
	const masked = restOfLetters.replace(/./g, "*");

	return firstLetter + masked + "@" + parts[1];
};
